import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Volume2 } from "lucide-react";
import Chip from "../../components/atoms/Chip";
import Card from "../../components/atoms/Card";

// Curated phrasebook for office English. Each phrase has a context, a
// "do say" phrasing, and a "don't say" foil. The 'don't' is the most
// common Indian-English-office mistake.
const CATEGORIES = [
  {
    id: "standup",
    label: "Daily standup",
    intro: "Three sentences: yesterday, today, blockers. Stop on time. Don't apologize for taking 20 seconds.",
    phrases: [
      { say: "Yesterday I finished the API integration. Today I'm starting the dashboard. No blockers.", avoid: "I tried to do the API, I think it's mostly done." },
      { say: "Quick update — the migration is still pending, expected by EOD.", avoid: "It's pending only, will do today itself." },
      { say: "I'm blocked on the design file. Could anyone share access?", avoid: "Design file not getting, please help anyone." },
    ],
  },
  {
    id: "disagree",
    label: "Polite pushback",
    intro: "Disagree without making it personal. Acknowledge, then redirect to the work.",
    phrases: [
      { say: "I see where you're coming from. I'd push back on the timeline — two weeks feels tight for the scope.", avoid: "No, no, that won't work, it's not possible in two weeks." },
      { say: "That's one option. Another approach would be to ship the MVP first, then iterate.", avoid: "Actually, your idea is not good, my idea is better." },
      { say: "Help me understand the constraint here — why does it need to be done this week?", avoid: "Why are you saying this? It doesn't make sense." },
    ],
  },
  {
    id: "meeting",
    label: "Meeting interjections",
    intro: "Get a word in without interrupting. The four phrases that work in any business meeting.",
    phrases: [
      { say: "Can I jump in for a second?", avoid: "Excuse me sir, one minute please sir." },
      { say: "Building on what Priya said — I'd add that…", avoid: "I have one point also." },
      { say: "Just to make sure I understood — you're saying X, right?", avoid: "I am not getting, please explain again." },
      { say: "Let's park that and come back to it — we're short on time.", avoid: "Leave it leave it, we'll see later." },
    ],
  },
  {
    id: "email",
    label: "Email tone",
    intro: "Office email isn't WhatsApp. Cut greetings, get to the point, sign off.",
    phrases: [
      { say: "Quick one — could you share the Q3 numbers by EOD?", avoid: "Sir, hope you are doing well. Please share the Q3 numbers at your convenience." },
      { say: "Heads up: I'll be out Wednesday.", avoid: "Just wanted to inform you that I will be on leave on Wednesday if that is okay with you." },
      { say: "Closing this thread — issue's resolved.", avoid: "Thanks a lot for the wonderful help, I really appreciate it from the bottom of my heart." },
    ],
  },
  {
    id: "presentation",
    label: "Presentations",
    intro: "Opening, transitions, closing. The three moments that decide whether anyone remembers your slides.",
    phrases: [
      { say: "I'll walk you through three things today: A, B, and C. Stop me if anything's unclear.", avoid: "So, today I'll be presenting on this topic, this is the agenda…" },
      { say: "That brings me to the second point —", avoid: "Next slide please." },
      { say: "To wrap up: A, B, and C. The one thing I'd ask for is your input on B by Friday.", avoid: "That's all from my side. Any questions?" },
    ],
  },
];

export default function OfficeEnglish() {
  const [active, setActive] = useState(CATEGORIES[0]);

  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/career" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Career
        </Link>
        <Chip tone="gold">Office English</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          The English your <em style={{ color: "var(--gold-500)" }}>manager already uses.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "62ch" }}>
          Standups, polite pushback, meeting interjections, email tone. Every phrase has a "do say" version and the Indian-English-office foil it replaces.
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {CATEGORIES.map((c) => (
          <button key={c.id} type="button" onClick={() => setActive(c)}
            style={{
              padding: "10px 18px",
              background: active.id === c.id ? "var(--ink-900)" : "#fff",
              color: active.id === c.id ? "var(--paper)" : "var(--fg-2)",
              border: `1px solid ${active.id === c.id ? "var(--ink-900)" : "var(--border-2)"}`,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
            }}>
            {c.label}
          </button>
        ))}
      </div>

      <p style={{ color: "var(--fg-2)", fontSize: 15.5, lineHeight: 1.6, margin: 0, maxWidth: "62ch", fontStyle: "italic" }}>{active.intro}</p>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {active.phrases.map((p, i) => <PhraseCard key={i} phrase={p} />)}
      </div>
    </div>
  );
}

function PhraseCard({ phrase }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard?.writeText(phrase.say);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function speak() {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(phrase.say);
      u.rate = 0.95;
      u.lang = "en-US";
      window.speechSynthesis.speak(u);
    }
  }

  return (
    <Card style={{ paddingTop: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--sage-dark)", textTransform: "uppercase", letterSpacing: "0.12em" }}>Do say</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" onClick={speak} aria-label="Play" style={{ background: "transparent", border: "1px solid var(--border-2)", color: "var(--fg-2)", padding: "5px 8px", borderRadius: 999, cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
            <Volume2 size={12} strokeWidth={1.6} />
          </button>
          <button type="button" onClick={copy} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "5px 10px", fontSize: 11, fontWeight: 600,
            background: copied ? "var(--sage)" : "#fff",
            color: copied ? "var(--paper)" : "var(--fg-2)",
            border: `1px solid ${copied ? "var(--sage)" : "var(--border-2)"}`,
            borderRadius: 999, cursor: "pointer",
          }}>
            {copied ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.6} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 17, lineHeight: 1.5, color: "var(--fg-1)", margin: "0 0 14px", fontFamily: "var(--font-display)" }}>
        "{phrase.say}"
      </p>

      <div style={{ paddingTop: 12, borderTop: "1px dashed var(--border-2)" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--berry-dark)", textTransform: "uppercase", letterSpacing: "0.12em" }}>Not</span>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: "var(--fg-3)", margin: "6px 0 0", textDecoration: "line-through", textDecorationColor: "var(--berry-light)", textDecorationThickness: "1px" }}>
          {phrase.avoid}
        </p>
      </div>
    </Card>
  );
}
